import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { Button, Card, FormField, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { hasSession, useAuthStore } from '../../lib/authStore.js';
import { useSiteContextStore } from '../../lib/siteContextStore.js';

interface LoginResponse {
  tokens: { accessToken: string; expiresIn: number };
  user: {
    id: string; tenantId: string; email: string; fullName: string;
    locale: 'en' | 'ar'; status: 'active' | 'invited' | 'disabled';
  };
  tenant: {
    id: string; name: string; slug: string; defaultLocale: 'en' | 'ar'; baseCurrencyCode: string;
  };
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  // Subscribed selector so a sign-out elsewhere re-renders this page.
  // Render-time redirect (not useEffect) so a signed-in user landing
  // on /login never sees the form flash before the bounce.
  const signedIn = useAuthStore(hasSession);
  // On a tenant's `app` custom domain, scope login to that tenant + brand the
  // page; on the platform host this is null (the manual slug field shows).
  const appTenant = useSiteContextStore((s) => (s.kind === 'app' ? s.tenant : null));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Tenant is resolved from the email at login; on an `app` custom domain the host
  // fixes it (sent below). No manual "workspace" field is shown.
  const tenantSlug = appTenant?.slug ?? '';
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (signedIn) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api().post<LoginResponse>('/auth/login', {
        email,
        password,
        ...(tenantSlug ? { tenantSlug } : {}),
      });
      useAuthStore.getState().setSession({
        accessToken: r.data.tokens.accessToken,
        expiresIn: r.data.tokens.expiresIn,
        user: r.data.user,
        tenant: r.data.tenant,
      });
      navigate('/dashboard');
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      // API codes are already namespaced (e.g. "auth.invalid_credentials", "rate_limited").
      // Look them up directly in the errors namespace — no extra "auth." prefix.
      setError(tc(`errors:${code ?? 'auth.unauthorized'}`, { defaultValue: tc('errors:server') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card size="lg">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold tracking-tight">{t('login.title')}</h1>
        <p className="mt-1.5 text-sm text-fg-muted">
          {appTenant
            ? t('login.signingInTo', { name: appTenant.name, defaultValue: `Sign in to ${appTenant.name}` })
            : t('login.subtitle')}
        </p>
      </div>

      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <FormField id="email" label={t('login.email')} required>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
          />
        </FormField>

        <FormField id="password" label={t('login.password')} required>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pe-16"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute inset-y-0 end-2 my-auto h-7 rounded px-2 text-xs font-medium text-fg-muted outline-none transition hover:text-fg focus-visible:ring-2 focus-visible:ring-brand/30"
              aria-pressed={showPassword}
              aria-label={showPassword
                ? t('login.hidePassword', { defaultValue: 'Hide password' })
                : t('login.showPassword', { defaultValue: 'Show password' })}
            >
              {showPassword
                ? t('login.hide', { defaultValue: 'Hide' })
                : t('login.show', { defaultValue: 'Show' })}
            </button>
          </div>
        </FormField>


        {error && (
          <div
            className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger"
            role="alert"
          >
            {error}
          </div>
        )}

        <Button type="submit" size="lg" loading={loading} className="w-full">
          {t('login.submit')}
        </Button>
      </form>

      <div className="my-7 h-px bg-border" />

      <p className="text-center text-sm text-fg-muted">
        {t('login.noAccount')}{' '}
        <Link
          to="/signup"
          className="font-medium text-brand underline-offset-4 hover:underline focus:outline-none focus-visible:underline"
        >
          {t('login.signUpLink')}
        </Link>
      </p>
    </Card>
  );
}
